import { MAX_LOOK_PIECES, type LookPiece, type LookSlot } from "./look-records.ts";

/**
 * The looks table as the game reads it (ADR-094 decision 1): the pieces in the table's order and each key's number. A
 * piece's number is its place counting from 1, which is what the wire carries; 0 is "nothing". The index is a `Map`,
 * so a key such as `constructor` or `__proto__` names no piece and reaches no prototype.
 */
export interface Looks {
  readonly pieces: readonly LookPiece[];
  readonly numbers: ReadonlyMap<string, number>;
}

/**
 * Indexes a looks table. The first of a key held twice keeps the key, though the validator admits no such table.
 *
 * @param pieces The pieces, in the table's order.
 * @returns The table and each key's number.
 * @throws RangeError when the table holds more pieces than a `u16` numbers.
 * @example
 * indexLooks(loadRules().tables.looks).numbers.get("humn-1");
 * // 1
 */
export function indexLooks(pieces: readonly LookPiece[]): Looks {
  if (pieces.length > MAX_LOOK_PIECES) {
    throw new RangeError(`a looks table of ${pieces.length} pieces is more than the ${MAX_LOOK_PIECES} a u16 numbers`);
  }
  const numbers = new Map<string, number>();
  for (let index = 0; index < pieces.length; index += 1) {
    const key = (pieces[index] as LookPiece).key;
    if (!numbers.has(key)) {
      numbers.set(key, index + 1);
    }
  }
  return Object.freeze({ pieces, numbers });
}

/**
 * A piece's number on the wire: its place in the table, counting from 1.
 *
 * @param looks The looks.
 * @param key The piece's key.
 * @returns Its number, or undefined when the table holds no such key.
 */
export function lookNumber(looks: Looks, key: string): number | undefined {
  return looks.numbers.get(key);
}

/**
 * The piece a key names.
 *
 * @param looks The looks.
 * @param key The piece's key.
 * @returns The piece, or undefined when the table holds no such key.
 */
export function lookByKey(looks: Looks, key: string): LookPiece | undefined {
  const number = looks.numbers.get(key);
  return number === undefined ? undefined : looks.pieces[number - 1];
}

/**
 * The piece a wire number names.
 *
 * @param looks The looks.
 * @param number The number, 1 or more.
 * @returns The piece, or undefined for 0, a number past the table or one that is not whole.
 */
export function lookByNumber(looks: Looks, number: number): LookPiece | undefined {
  return Number.isInteger(number) && number >= 1 ? looks.pieces[number - 1] : undefined;
}

/**
 * The number a slot falls back to when a look names a piece the table lacks or one of another slot (ADR-094 decision
 * 10): the first skin, the first outfit, and nothing for hair, a hat and the hands, so a character is never without a
 * body or undressed.
 *
 * @param looks The looks.
 * @param slot The slot.
 * @returns The fallback piece's number, or 0 for nothing.
 */
export function fallbackNumber(looks: Looks, slot: LookSlot): number {
  if (slot !== "skin" && slot !== "outfit") {
    return 0;
  }
  return looks.pieces.findIndex((piece) => piece.slot === slot) + 1;
}
